import type { Campaign, CampaignInsightsSummary } from '../../api/types';
import { formatCampaignMoney } from '../../utils/format-campaign-money';
import { getCampaignResultConfig } from './campaign-presentation';

export { formatCampaignMoney } from '../../utils/format-campaign-money';

type Props = {
  campaign: Campaign;
  summary?: CampaignInsightsSummary;
  loading: boolean;
  failed: boolean;
  prominent?: boolean;
  period?: string;
};

export function CampaignSpend({
  campaign,
  summary,
  loading,
  failed,
  prominent,
}: Props) {
  return (
    <span
      className={`tabular-nums ${prominent ? 'text-base font-semibold text-foreground' : 'text-sm'}`}
    >
      {loading
        ? '…'
        : failed
          ? 'Unavailable'
          : formatCampaignMoney(summary?.totals.spend ?? 0, campaign.currency)}
    </span>
  );
}

export function CampaignCostPerResult({
  campaign,
  summary,
  loading,
  failed,
  prominent,
  period = 'the campaign’s lifetime',
}: Props) {
  const { pick, costUnit, plural, scale } = getCampaignResultConfig(campaign);
  const results = summary ? pick(summary.totals) : 0;
  const title =
    failed || results === undefined
      ? 'Performance data could not be loaded.'
      : results === 0
        ? `No ${plural} yet.`
        : `Amount spent divided by ${plural} over ${period}${scale === 1000 ? ', multiplied by 1,000' : ''}.`;
  const value = loading
    ? '…'
    : failed || results === undefined || results === 0
      ? '—'
      : formatCampaignMoney(
          ((summary?.totals.spend ?? 0) / results) * scale,
          campaign.currency
        );
  return (
    <span
      className={`tabular-nums ${prominent ? 'text-base font-semibold text-foreground' : 'text-sm font-medium'}`}
      title={title}
      aria-label={`Cost per ${costUnit}: ${value}. ${title}`}
    >
      {value}
      <span className="mt-0.5 block text-xs font-normal text-muted-foreground">
        per {costUnit}
      </span>
    </span>
  );
}

export function CampaignCostPerLead({
  campaign,
  summary,
  loading,
  failed,
}: Props) {
  const leads = summary?.totals.leads ?? 0;
  const value = loading
    ? '…'
    : failed || leads === 0
      ? '—'
      : formatCampaignMoney(
          (summary?.totals.spend ?? 0) / leads,
          campaign.currency
        );
  return (
    <span
      className="text-sm tabular-nums"
      title={
        failed
          ? 'Performance data could not be loaded.'
          : leads === 0
            ? 'No leads yet.'
            : 'Amount spent divided by Meta-reported leads.'
      }
    >
      {value}
    </span>
  );
}

export function CampaignViews({ summary, loading, failed }: Props) {
  return (
    <span className="text-sm tabular-nums" title="Impressions reported by Meta">
      {loading
        ? '…'
        : failed
          ? 'Unavailable'
          : (summary?.totals.impressions ?? 0).toLocaleString()}
    </span>
  );
}

/** Totals for one campaign or one ad. Missing fields come from older APIs. */
export type MetricTotals = Partial<CampaignInsightsSummary['totals']>;

export type CoreMetricId = 'cpc' | 'cpm' | 'clicks' | 'frequency' | 'cpl';

/** The metrics every campaign and ad row shows, in display order. */
export const CORE_METRICS: { id: CoreMetricId; label: string }[] = [
  { id: 'cpc', label: 'CPC' },
  { id: 'cpm', label: 'CPM' },
  { id: 'clicks', label: 'Clicks' },
  { id: 'frequency', label: 'Frequency' },
  { id: 'cpl', label: 'CPL' },
];

/**
 * A metric as display text. A dash means unavailable, or a cost with nothing
 * to divide by — Meta reports CPC/CPM as 0 when there were no clicks or views,
 * which would otherwise read as free delivery.
 */
export function formatCoreMetric(
  id: CoreMetricId,
  totals: MetricTotals | undefined,
  currency: string | undefined
): string {
  const clicks = totals?.clicks ?? 0;
  const impressions = totals?.impressions ?? 0;
  switch (id) {
    case 'cpc':
      return clicks > 0 && totals?.cpc !== undefined
        ? formatCampaignMoney(totals.cpc, currency)
        : '—';
    case 'cpm':
      return impressions > 0 && totals?.cpm !== undefined
        ? formatCampaignMoney(totals.cpm, currency)
        : '—';
    case 'clicks':
      return clicks.toLocaleString();
    case 'frequency':
      return impressions > 0 && totals?.frequency !== undefined
        ? totals.frequency.toLocaleString(undefined, {
            maximumFractionDigits: 2,
          })
        : '—';
    case 'cpl':
      return totals?.leads
        ? formatCampaignMoney((totals.spend ?? 0) / totals.leads, currency)
        : '—';
  }
}

export function CoreMetric({
  id,
  totals,
  currency,
  loading,
  failed,
}: {
  id: CoreMetricId;
  totals?: MetricTotals;
  currency?: string;
  loading: boolean;
  failed: boolean;
}) {
  return (
    <span
      className="text-sm tabular-nums"
      title={failed ? 'Performance data could not be loaded.' : undefined}
    >
      {loading ? '…' : failed ? '—' : formatCoreMetric(id, totals, currency)}
    </span>
  );
}

/** The old phone-row result ("81 leads"): a dash before any delivery. */
export function ResultCount({
  campaign,
  totals,
  loading,
  failed,
}: {
  campaign: Campaign;
  totals?: MetricTotals;
  loading: boolean;
  failed: boolean;
}) {
  const { singular, plural, pick } = getCampaignResultConfig(campaign);
  if (loading) return <div className="text-muted-foreground text-sm">...</div>;
  const count =
    totals && !failed
      ? pick(totals as CampaignInsightsSummary['totals'])
      : undefined;
  if (count === undefined)
    return (
      <div className="text-muted-foreground text-sm">
        &mdash; <span className="text-xs">{plural}</span>
      </div>
    );
  return (
    <div className="text-sm">
      <span className="font-medium tabular-nums">{count.toLocaleString()}</span>{' '}
      <span className="text-muted-foreground text-xs">
        {count === 1 ? singular : plural}
      </span>
    </div>
  );
}
